import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconShoppingCart, IconTag } from '@tabler/icons-react';
import { ROUTES } from '@/routes/paths';

interface StartTradingCardProps {
  /** 'sell' — производителю: создать предложение о продаже; 'buy' — не-производителю: создать прайс-лист. */
  readonly mode: 'sell' | 'buy';
  /** true — пользователь уже торговал (нет активного листинга, но был раньше): напоминание, а не «первое». */
  readonly returning?: boolean;
}

/**
 * Напоминание-CTA на Главной для аттестованной организации без первого листинга: сельхозтоваро-
 * производителю — создать предложение о продаже, остальным — создать прайс-лист. Визуальный язык — как
 * {@link AddEmployeesCard}: карточка + lime-иконка + заголовок/подсказка + основная кнопка-диплинк.
 * Самоскрывается, когда листинг создан (условие показа — в AppHome).
 */
export default function StartTradingCard({ mode, returning = false }: StartTradingCardProps) {
  const { t } = useTranslation();
  const sell = mode === 'sell';
  const to = sell ? ROUTES.APP_SELL_NEW : ROUTES.APP_BUY_NEW;
  const Icon = sell ? IconTag : IconShoppingCart;
  const base = sell ? 'app.home.startTrading.sell' : 'app.home.startTrading.buy';
  // Уже торговавшему — напоминание («нет активного листинга»), новичку — приглашение создать ПЕРВЫЙ.
  const titleKey = returning ? `${base}.titleAgain` : `${base}.title`;
  const hintKey = returning ? `${base}.hintAgain` : `${base}.hint`;

  return (
    <Card withBorder radius="md" padding="lg">
      <Group justify="space-between" align="center" wrap="wrap" gap="md">
        <Group align="center" wrap="nowrap" gap="md" style={{ minWidth: 0 }}>
          <ThemeIcon variant="light" color="lime" size={44} radius="md">
            <Icon size={24} />
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={600}>{t(titleKey)}</Text>
            <Text c="gray.7" size="sm">
              {t(hintKey)}
            </Text>
          </Stack>
        </Group>
        <Button component={Link} to={to} leftSection={<Icon size={16} />}>
          {t(`${base}.cta`)}
        </Button>
      </Group>
    </Card>
  );
}
